import { Link } from "react-router-dom";
import { useEffect, useState } from "react";
import { useAuth } from "@/context/AuthContext";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { buildAvatarUrl } from "@/components/ProfileImageUpload";
import { EmailLimitsSetting } from "@/components/EmailLimitsSetting";
import {
  ShieldCheck, Users, FileText, Sliders, Mail, ClipboardList,
  Settings, ArrowRight, Sparkles, Activity, FileSearch, CreditCard, BarChart3, Trash2, Mailbox, Bell, Download, Type, MapPin, Clock, UserCheck, X, Ban, Image as ImageIcon, Ticket, Wrench, PoundSterling, Trophy
} from "lucide-react";

/**
 * BulkZeroPriceFixerCard (iter165) — Admin one-tap repair for group bookings
 * that were approved with £0 total (coach's group_session_rate wasn't set at
 * request time). Hits POST /api/admin/group-bookings/bulk-fix-zero-price and
 * shows a per-booking result summary so admin can see what was fixed and
 * what was skipped (paid share = 409, coach with no rate = skipped).
 */
function BulkZeroPriceFixerCard() {
  const [running, setRunning] = useState(false);
  const [lastResult, setLastResult] = useState(null);
  const runFix = async () => {
    setRunning(true);
    try {
      const { data } = await api.post("/admin/group-bookings/bulk-fix-zero-price");
      setLastResult(data);
      const fixed = data?.fixed || 0;
      const skipped = data?.skipped || 0;
      if (fixed && !skipped) toast.success(`Fixed ${fixed} booking${fixed === 1 ? "" : "s"}`);
      else if (fixed && skipped) toast.warning(`Fixed ${fixed}, skipped ${skipped} — see details below`);
      else toast.info("No £0 approved bookings found — nothing to fix");
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail) || "Bulk fix failed");
    } finally { setRunning(false); }
  };
  return (
    <section
      className="card-surface p-6 fade-up border-l-4 border-[#FF9F1C]"
      data-testid="bulk-zero-price-card"
    >
      <div className="flex items-start gap-3 mb-3">
        <PoundSterling className="w-5 h-5 text-[#FF9F1C] mt-0.5 shrink-0" />
        <div className="flex-1">
          <h3 className="h-display text-2xl mb-1">Fix stale £0 bookings</h3>
          <p className="text-xs text-[#A3A3A3] max-w-2xl leading-relaxed">
            One-shot repair for approved group bookings sitting at £0 because a coach's group rate wasn't set when the request cascaded. For each row: reads the coach's current rate, computes <span className="text-white">rate × duration ÷ 60</span>, and updates every pending share. Skips bookings where a share is already paid.
          </p>
        </div>
        <button
          type="button"
          onClick={runFix}
          disabled={running}
          data-testid="bulk-zero-price-run"
          className="px-4 py-2 text-xs uppercase tracking-wider bg-[#FF9F1C] text-black hover:opacity-90 disabled:opacity-40 shrink-0 inline-flex items-center gap-1.5"
        >
          <Wrench className="w-3.5 h-3.5" /> {running ? "Fixing…" : "Run fix"}
        </button>
      </div>
      {lastResult && (
        <div className="text-xs mt-2 space-y-2" data-testid="bulk-zero-price-result">
          <div className="flex gap-2 flex-wrap">
            <span className="label-mono px-2 py-0.5 text-[#CCFF00]">FIXED · {lastResult.fixed}</span>
            <span className="label-mono px-2 py-0.5 text-[#FF9F1C]">SKIPPED · {lastResult.skipped}</span>
          </div>
          {(lastResult.results || []).length > 0 && (
            <ul className="space-y-1 max-h-40 overflow-y-auto pr-1">
              {lastResult.results.map((r) => (
                <li key={r.id} className="flex items-center gap-2 text-[11px] text-[#A3A3A3]" data-testid={`bulk-zero-row-${r.id}`}>
                  <span className={r.ok ? "text-[#CCFF00]" : "text-[#FF9F1C]"}>
                    {r.ok ? "✓" : "!"}
                  </span>
                  <Link to={`/group-bookings/${r.id}`} className="hover:text-white truncate max-w-[240px]">{r.id}</Link>
                  <span className="ml-auto">
                    {r.ok
                      ? `£${r.new_price} · ${r.shares_updated} share${r.shares_updated === 1 ? "" : "s"}`
                      : r.reason}
                  </span>
                </li>
              ))}
            </ul>
          )}
        </div>
      )}
    </section>
  );
}

const TOOLS = [
  {
    group: "INSIGHTS",
    items: [
      {
        to: "/admin/stats",
        title: "Stats & KPIs",
        desc: "Live platform metrics — users, bookings, revenue, 8-week trend, top coaches. Refresh on demand.",
        icon: BarChart3,
        accent: "#CCFF00",
        requires: "view_stats",
      },
      {
        to: "/admin/pending-offers",
        title: "Pending offers monitor",
        desc: "Every unpaid package awaiting a student decision — sorted most-at-risk first. Spot coaches whose offers stall so you can follow up before the 14-day auto-expiry hits.",
        icon: Clock,
        accent: "#FF4500",
        requires: "view_stats",
      },
    ],
  },
  {
    group: "REVIEW & APPROVALS",
    items: [
      {
        to: "/admin/applications",
        title: "Coach applications",
        desc: "Approve or reject coach signups. Each application includes insurance + certification documents.",
        icon: ShieldCheck,
        accent: "#CCFF00",
        requires: "approve_coaches",
      },
      {
        to: "/admin/emails",
        title: "Bulk email campaigns",
        desc: "Blast a message to players filtered by sex, DUPR level, session history or specific offering attendees. Preview count, mask emails, then send via Resend + notify.",
        icon: Mail,
        accent: "#FF4500",
        requires: "manage_users",
      },
      {
        to: "/admin/email-diagnostics",
        title: "Email diagnostics",
        desc: "See whether outbound emails (welcome, follow-ups, bulk) are actually being delivered — or silently mocked. Live env-var check + last 50 sends with pass/fail status.",
        icon: ClipboardList,
        accent: "#CCFF00",
        requires: "manage_users",
      },
      {
        to: "/admin/audit",
        title: "Audit log",
        desc: "Full history of permission changes and Stripe config edits — who promoted whom, when, and what the previous state was.",
        icon: FileSearch,
        accent: "#FF4500",
        requires: "view_audit_log",
      },
    ],
  },
  {
    group: "USERS & ACCESS",
    items: [
      {
        to: "/admin/users",
        title: "Users & roles",
        desc: "Click any user to view their full profile (bookings, AI reports, offerings) and edit granular permissions. Promote a coach to handle approvals while you're away without giving them full admin.",
        icon: Users,
        accent: "#CCFF00",
        requires: "manage_users",
      },
    ],
  },
  {
    group: "PLATFORM SETTINGS",
    items: [
      {
        to: "/admin/features",
        title: "Features",
        desc: "Toggle Squash visibility, pricing display, and other platform-wide flags. Squash is OFF by default.",
        icon: Sliders,
        accent: "#FF4500",
        requires: "manage_features",
      },
      {
        to: "/settings/pricing",
        title: "Pricing",
        desc: "Edit per-sport, per-tier pricing — Group / Performance / 1:1. Players see this on the public site.",
        icon: ClipboardList,
        accent: "#CCFF00",
        requires: "manage_pricing",
      },
      {
        to: "/admin/stripe",
        title: "Stripe configuration",
        desc: "Swap between test and live Stripe keys directly from the UI. Changes are audit-logged.",
        icon: CreditCard,
        accent: "#FF4500",
        requires: "manage_stripe",
      },
      {
        to: "/admin/landing",
        title: "Landing page typography",
        desc: "Pick the heading and body fonts shown on the public homepage. Pre-curated Google Fonts only — locked whitelist for safety. Changes go live for new visitors immediately.",
        icon: Type,
        accent: "#CCFF00",
        requires: "super_admin",
      },
      {
        to: "/admin/regions",
        title: "Regions & areas",
        desc: "Curate the geographic areas coaches can operate in (e.g. North Wales · Holyhead to Colwyn Bay). Approve pending region requests when coaches ask to expand their patch.",
        icon: MapPin,
        accent: "#FF4500",
        requires: "approve_coaches",
      },
      {
        to: "/admin/media-consent",
        title: "Media consent",
        desc: "Edit the media-consent wording every player and coach sees. Bump the version to force everyone to re-answer (GDPR-safe when scope changes).",
        icon: ClipboardList,
        accent: "#CCFF00",
        requires: "super_admin",
      },
      {
        to: "/admin/promo-codes",
        title: "Promo codes",
        desc: "Create % or £-off codes for Package Store checkouts. One-time-use per student, soft-deactivation keeps the audit trail intact.",
        icon: Ticket,
        accent: "#CCFF00",
        requires: "manage_pricing",
      },
    ],
  },
  {
    group: "OPERATIONAL",
    items: [
      {
        to: "/admin/festivals",
        title: "Festival Manager",
        desc: "List every festival across every organiser with status, dates, pack purchased, and player count. Open any organiser's festival to edit config, adjust helper allocations, extend expired passes, and set organiser-wide bespoke pricing. Every Owner change is logged and shown to the organiser.",
        icon: Trophy,
        accent: "#CCFF00",
        requires: "super_admin",
      },
      {
        to: "/admin/inbox",
        title: "Email & Comms log",
        desc: "Every email the platform tried to send (password resets, booking confirmations). Use this to copy reset URLs while email is mocked.",
        icon: Mail,
        accent: "#CCFF00",
        requires: "view_only_admin",
      },
      {
        to: "/players",
        title: "All players",
        desc: "Admins see every player on the platform. Regular coaches only see players who've booked with them.",
        icon: Activity,
        accent: "#FF4500",
        requires: "view_only_admin",
      },
      {
        to: "/offerings",
        title: "All offerings",
        desc: "Every session published by every coach. Use this to spot-check pricing or remove offerings on behalf of a coach.",
        icon: FileText,
        accent: "#CCFF00",
        requires: "view_only_admin",
      },
    ],
  },
];

export default function AdminOverview() {
  const { user } = useAuth();
  const [wiping, setWiping] = useState(false);
  const [runningJobs, setRunningJobs] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [pendingPlayers, setPendingPlayers] = useState([]);
  const [pendingAvatars, setPendingAvatars] = useState([]);
  const [reviewingAvatar, setReviewingAvatar] = useState({}); // id -> 'approve'|'reject'
  const [blockedSignups, setBlockedSignups] = useState([]);
  const [reviewing, setReviewing] = useState({});  // id -> 'approve' | 'reject' while in-flight
  const [resetting, setResetting] = useState({});  // email -> boolean while in-flight
  const [seedingDrills, setSeedingDrills] = useState(false);
  const [migratingHistory, setMigratingHistory] = useState(false);
  const [healingAssessments, setHealingAssessments] = useState(false);
  const [bulkApproving, setBulkApproving] = useState(false);
  const perms = new Set(user?.permissions || []);
  if (user?.role === "admin") perms.add("admin");
  const isSuperAdmin = perms.has("super_admin");
  // Mirror backend expansion: super_admin → all granular perms; admin → mid-tier set.
  const ADMIN_GRANTS = new Set(["approve_coaches", "manage_pricing", "manage_features", "view_stats", "view_only_admin"]);
  const SUPER_ADMIN_GRANTS = new Set([
    "approve_coaches", "manage_users", "manage_pricing", "manage_features",
    "manage_stripe", "view_stats", "view_audit_log", "view_only_admin",
  ]);
  const has = (p) => {
    if (perms.has(p)) return true;
    if (isSuperAdmin && SUPER_ADMIN_GRANTS.has(p)) return true;
    if (perms.has("admin") && ADMIN_GRANTS.has(p)) return true;
    return false;
  };
  const isAdmin = perms.has("admin") || isSuperAdmin;

  const canManageUsers = has("manage_users");

  useEffect(() => {
    if (!canManageUsers) return;
    api.get("/admin/players/pending").then((r) => setPendingPlayers(r.data || [])).catch(() => setPendingPlayers([]));
    api.get("/admin/avatars/pending").then((r) => setPendingAvatars(r.data || [])).catch(() => setPendingAvatars([]));
    api.get("/admin/registration-attempts").then((r) => setBlockedSignups(r.data || [])).catch(() => setBlockedSignups([]));
  }, [canManageUsers]);

  const decideAvatar = async (userId, action) => {
    let reason = "";
    if (action === "reject") {
      reason = window.prompt("Optional — tell them why the photo wasn't accepted. This is sent to them.") || "";
    }
    setReviewingAvatar((s) => ({ ...s, [userId]: action }));
    try {
      const url = action === "approve"
        ? `/admin/users/${userId}/avatar/approve`
        : `/admin/users/${userId}/avatar/reject`;
      await api.post(url, action === "reject" ? { reason } : {});
      setPendingAvatars((list) => list.filter((a) => a.id !== userId));
      toast.success(action === "approve" ? "Photo approved" : "Photo declined");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Could not update");
    } finally {
      setReviewingAvatar((s) => { const n = { ...s }; delete n[userId]; return n; });
    }
  };

  const resetSignup = async (email) => {
    if (!window.confirm(`Reset abandon count for ${email}? They'll be able to register again.`)) return;
    setResetting((s) => ({ ...s, [email]: true }));
    try {
      await api.post(`/admin/registration-attempts/${encodeURIComponent(email)}/reset`);
      setBlockedSignups((list) => list.filter((b) => b.email !== email));
      toast.success(`${email} unlocked`);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Could not reset");
    } finally {
      setResetting((s) => { const n = { ...s }; delete n[email]; return n; });
    }
  };

  // Iter91 — bulk actions on the Registration Attempts queue.
  const [selectedSignups, setSelectedSignups] = useState(() => new Set());
  const [bulkBusy, setBulkBusy] = useState(false);
  const toggleSelectSignup = (email) => setSelectedSignups((prev) => {
    const n = new Set(prev);
    if (n.has(email)) n.delete(email); else n.add(email);
    return n;
  });
  const toggleSelectAllSignups = (list) => setSelectedSignups((prev) => {
    if (prev.size === list.length) return new Set();
    return new Set(list.map((b) => b.email));
  });
  const bulkAction = async (kind) => {
    const emails = Array.from(selectedSignups);
    if (emails.length === 0) return;
    const verb = kind === "reset" ? "reset" : "permanently remove";
    if (!window.confirm(`${kind === "reset" ? "Reset" : "Remove"} ${emails.length} attempt${emails.length === 1 ? "" : "s"}? This will ${verb} them.`)) return;
    setBulkBusy(true);
    try {
      const url = kind === "reset"
        ? "/admin/registration-attempts/bulk-reset"
        : "/admin/registration-attempts/bulk-delete";
      const { data } = await api.post(url, { emails });
      setBlockedSignups((list) => list.filter((b) => !selectedSignups.has(b.email)));
      setSelectedSignups(new Set());
      toast.success(`${data.deleted || emails.length} attempt${(data.deleted || emails.length) === 1 ? "" : "s"} ${kind === "reset" ? "reset" : "removed"}`);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Bulk action failed");
    } finally {
      setBulkBusy(false);
    }
  };

  const decide = async (playerId, action) => {
    let reason = "";
    if (action === "reject") {
      reason = window.prompt("Optional — tell the player why. This is emailed to them.") || "";
    }
    setReviewing((s) => ({ ...s, [playerId]: action }));
    try {
      const url = action === "approve"
        ? `/admin/users/${playerId}/approve-player`
        : `/admin/users/${playerId}/reject-player`;
      await api.post(url, action === "reject" ? { reason } : {});
      setPendingPlayers((list) => list.filter((p) => p.id !== playerId));
      toast.success(action === "approve" ? "Player approved — they can book now" : "Player declined");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Could not update");
    } finally {
      setReviewing((s) => { const n = { ...s }; delete n[playerId]; return n; });
    }
  };

  const approveAll = async () => {
    const n = pendingPlayers.length;
    if (n === 0) return;
    if (!window.confirm(`Approve ALL ${n} pending player${n === 1 ? "" : "s"}? Each will get a welcome notification and can book immediately. This can't be undone.`)) return;
    setBulkApproving(true);
    try {
      const res = await api.post("/admin/players/approve-all");
      const count = res?.data?.approved_count ?? n;
      setPendingPlayers([]);
      toast.success(`Approved ${count} player${count === 1 ? "" : "s"} — they can book now.`);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Bulk approve failed");
    } finally {
      setBulkApproving(false);
    }
  };

  // Human-friendly "waiting Xh/Xd" label for the queue rows. Colour-codes any
  // signup that's been waiting >2 days so admins prioritise stale ones.
  const waitingLabel = (createdAt) => {
    if (!createdAt) return { text: "—", stale: false };
    const ms = Date.now() - new Date(createdAt).getTime();
    if (Number.isNaN(ms) || ms < 0) return { text: "—", stale: false };
    const hours = Math.floor(ms / 36e5);
    const days = Math.floor(hours / 24);
    const stale = days >= 2;
    if (days >= 1) return { text: `waiting ${days}d`, stale };
    if (hours >= 1) return { text: `waiting ${hours}h`, stale };
    const mins = Math.max(1, Math.floor(ms / 6e4));
    return { text: `waiting ${mins}m`, stale };
  };

  // Deep-link support — CoachDashboard/Layout link to /admin#pending-players.
  // Scroll to the widget once it's rendered so admins land on it directly.
  useEffect(() => {
    if (typeof window === "undefined") return;
    if (window.location.hash !== "#pending-players") return;
    if (pendingPlayers.length === 0) return;
    const el = document.getElementById("pending-players");
    if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
  }, [pendingPlayers.length]);

  const canSee = (req) => {
    if (req === "super_admin") return isSuperAdmin;
    if (req === "admin") return isAdmin;
    return has(req);
  };

  return (
    <div className="space-y-10" data-testid="admin-overview-page">
      <div className="fade-up">
        <div className="label-mono mb-2" style={{ color: "#CCFF00" }}>ADMIN CONSOLE</div>
        <h1 className="h-display text-5xl tracking-tighter">Welcome, {user?.name?.split(" ")[0] || "Admin"}.</h1>
        <p className="text-sm text-[#A3A3A3] mt-3 max-w-2xl">
          Every platform-wide control lives here. Pick a tool below — each card explains what it does in plain English so you don&apos;t have to guess.
        </p>
        <div className="flex flex-wrap items-center gap-2 mt-4">
          <span className="label-mono px-2 py-1 border border-[#262626]">ROLE · {user?.role?.toUpperCase()}</span>
          {(user?.permissions || []).map((p) => (
            <span key={p} className="label-mono px-2 py-1" style={{ background: "#CCFF00", color: "#0A0A0A", fontWeight: 700 }}>{p.toUpperCase()}</span>
          ))}
        </div>
      </div>

      <EmailLimitsSetting />

      {/* Player approval queue — new signups sit in 'pending' until reviewed. */}
      {canManageUsers && (
        <BulkZeroPriceFixerCard />
      )}
      {canManageUsers && pendingPlayers.length > 0 && (
        <section id="pending-players" className="card-surface p-6 fade-up border-l-4 border-[#CCFF00]" data-testid="pending-players-widget">
          <div className="flex items-start gap-3 mb-4">
            <UserCheck className="w-5 h-5 text-[#CCFF00] mt-0.5 shrink-0" />
            <div className="flex-1">
              <div className="flex items-center gap-2 mb-1 flex-wrap">
                <h3 className="h-display text-2xl">Players awaiting approval</h3>
                <span className="label-mono px-2 py-0.5" style={{ background: "#CCFF00", color: "#0A0A0A", fontWeight: 700 }} data-testid="pending-players-count">
                  {pendingPlayers.length}
                </span>
              </div>
              <p className="text-xs text-[#A3A3A3] leading-relaxed max-w-2xl">
                New signups can&apos;t book until you approve them here. Approve to unlock the full app, or decline with a reason (emailed to them).
              </p>
            </div>
            <button
              type="button"
              disabled={bulkApproving || pendingPlayers.length === 0}
              onClick={approveAll}
              className="px-3 py-1.5 text-xs uppercase tracking-wider bg-[#CCFF00] text-black hover:opacity-90 inline-flex items-center gap-1 disabled:opacity-40 shrink-0"
              data-testid="approve-all-players"
              title={`Approve every one of the ${pendingPlayers.length} pending players`}
            >
              <UserCheck className="w-3.5 h-3.5" /> {bulkApproving ? "Approving…" : `Approve all (${pendingPlayers.length})`}
            </button>
          </div>
          <div className="space-y-2 max-h-[420px] overflow-y-auto pr-1">
            {pendingPlayers.map((p) => {
              const wait = waitingLabel(p.created_at);
              return (
              <div key={p.id} className="border border-[#262626] p-4 flex flex-wrap items-center justify-between gap-3" data-testid={`pending-player-${p.id}`}>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-1">
                    <Link to={`/admin/users/${p.id}`} className="font-semibold hover:text-[#CCFF00] truncate" data-testid={`pending-player-name-${p.id}`}>
                      {p.name || "(no name)"}
                    </Link>
                    <span className="text-xs text-[#525252]">·</span>
                    <span className="text-xs text-[#A3A3A3] truncate">{p.email}</span>
                  </div>
                  <div className="text-[10px] uppercase tracking-wider text-[#525252] flex flex-wrap items-center gap-2">
                    <span>signed up {p.created_at ? new Date(p.created_at).toLocaleDateString() : "—"}</span>
                    <span
                      data-testid={`pending-player-wait-${p.id}`}
                      className={`inline-flex items-center gap-1 px-1.5 py-0.5 border ${wait.stale ? "border-[#FF4500] text-[#FF4500]" : "border-[#262626] text-[#A3A3A3]"}`}
                    >
                      <Clock className="w-3 h-3" strokeWidth={1.75} /> {wait.text}
                    </span>
                    {p.personal_details?.postcode && <span>· {p.personal_details.postcode}</span>}
                  </div>
                </div>
                <div className="flex gap-2 shrink-0">
                  <button
                    type="button"
                    disabled={!!reviewing[p.id] || bulkApproving}
                    onClick={() => decide(p.id, "approve")}
                    className="px-3 py-1.5 text-xs uppercase tracking-wider bg-[#CCFF00] text-black hover:opacity-90 inline-flex items-center gap-1 disabled:opacity-40"
                    data-testid={`approve-player-${p.id}`}
                  >
                    <UserCheck className="w-3.5 h-3.5" /> {reviewing[p.id] === "approve" ? "…" : "Approve"}
                  </button>
                  <button
                    type="button"
                    disabled={!!reviewing[p.id] || bulkApproving}
                    onClick={() => decide(p.id, "reject")}
                    className="px-3 py-1.5 text-xs uppercase tracking-wider border border-[#FF4500] text-[#FF4500] hover:bg-[#FF4500] hover:text-black inline-flex items-center gap-1 disabled:opacity-40"
                    data-testid={`reject-player-${p.id}`}
                  >
                    <X className="w-3.5 h-3.5" /> {reviewing[p.id] === "reject" ? "…" : "Decline"}
                  </button>
                </div>
              </div>
              );
            })}
          </div>
        </section>
      )}

      {/* Profile-photo moderation queue — greylist from Claude Vision. */}
      {canManageUsers && pendingAvatars.length > 0 && (
        <section id="pending-avatars" className="card-surface p-6 fade-up border-l-4 border-[#CCFF00]" data-testid="pending-avatars-widget">
          <div className="flex items-start gap-3 mb-4">
            <ImageIcon className="w-5 h-5 text-[#CCFF00] mt-0.5 shrink-0" strokeWidth={1.5} />
            <div className="flex-1">
              <div className="flex items-center gap-2 mb-1 flex-wrap">
                <h3 className="h-display text-2xl">Profile photos awaiting review</h3>
                <span className="label-mono px-2 py-0.5" style={{ background: "#CCFF00", color: "#0A0A0A", fontWeight: 700 }} data-testid="pending-avatars-count">
                  {pendingAvatars.length}
                </span>
              </div>
              <p className="text-xs text-[#A3A3A3] leading-relaxed max-w-2xl">
                AI flagged these photos as unclear. Approve to publish, or decline with a short note (sent to the user). Users see initials meanwhile.
              </p>
            </div>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 max-h-[560px] overflow-y-auto pr-1">
            {pendingAvatars.map((a) => {
              const imgUrl = buildAvatarUrl(a.avatar_pending_path);
              return (
                <div key={a.id} className="border border-[#262626] p-3 flex gap-3" data-testid={`pending-avatar-${a.id}`}>
                  <img
                    src={imgUrl} alt={a.name || a.email}
                    className="w-20 h-20 object-cover border border-[#262626] shrink-0"
                    data-testid={`pending-avatar-img-${a.id}`}
                    onError={(e) => { e.currentTarget.style.display = "none"; }}
                  />
                  <div className="flex-1 min-w-0 flex flex-col justify-between">
                    <div>
                      <Link to={`/admin/users/${a.id}`} className="font-semibold hover:text-[#CCFF00] truncate block" data-testid={`pending-avatar-name-${a.id}`}>
                        {a.name || "(no name)"}
                      </Link>
                      <div className="text-xs text-[#A3A3A3] truncate">{a.email}</div>
                      {a.avatar_review_reason && (
                        <div className="mt-1 text-[10px] uppercase tracking-wider text-[#A3A3A3] leading-snug">
                          AI: {a.avatar_review_reason}
                        </div>
                      )}
                    </div>
                    <div className="flex gap-1.5 mt-2">
                      <button
                        type="button"
                        disabled={!!reviewingAvatar[a.id]}
                        onClick={() => decideAvatar(a.id, "approve")}
                        className="flex-1 px-2 py-1.5 text-[10px] uppercase tracking-wider bg-[#CCFF00] text-black hover:opacity-90 inline-flex items-center justify-center gap-1 disabled:opacity-40"
                        data-testid={`approve-avatar-${a.id}`}
                      >
                        <UserCheck className="w-3 h-3" /> {reviewingAvatar[a.id] === "approve" ? "…" : "Approve"}
                      </button>
                      <button
                        type="button"
                        disabled={!!reviewingAvatar[a.id]}
                        onClick={() => decideAvatar(a.id, "reject")}
                        className="flex-1 px-2 py-1.5 text-[10px] uppercase tracking-wider border border-[#FF4500] text-[#FF4500] hover:bg-[#FF4500] hover:text-black inline-flex items-center justify-center gap-1 disabled:opacity-40"
                        data-testid={`reject-avatar-${a.id}`}
                      >
                        <X className="w-3 h-3" /> {reviewingAvatar[a.id] === "reject" ? "…" : "Decline"}
                      </button>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </section>
      )}

      {/* Blocked signups — email addresses with 3+ abandoned 48h windows. */}
      {canManageUsers && blockedSignups.length > 0 && (() => {
        const blocked = blockedSignups.filter((b) => (b.abandon_count || 0) >= 3);
        const warned = blockedSignups.filter((b) => (b.abandon_count || 0) < 3);
        return (
          <section className="card-surface p-6 fade-up border-l-4" style={{ borderColor: blocked.length > 0 ? "#EF4444" : "#FF4500" }} data-testid="blocked-signups-widget">
            <div className="flex items-start gap-3 mb-4">
              <Ban className="w-5 h-5 mt-0.5 shrink-0" style={{ color: blocked.length > 0 ? "#EF4444" : "#FF4500" }} />
              <div className="flex-1">
                <div className="flex items-center gap-2 mb-1">
                  <h3 className="h-display text-2xl">Registration attempts</h3>
                  {blocked.length > 0 && (
                    <span className="label-mono px-2 py-0.5 bg-[#EF4444] text-white" style={{ fontWeight: 700 }} data-testid="blocked-signups-count">
                      {blocked.length} blocked
                    </span>
                  )}
                </div>
                <p className="text-xs text-[#A3A3A3] leading-relaxed max-w-2xl">
                  Emails that abandoned the 48h profile window. Three strikes puts the address on hold until you reset it. Attempts older than 30 days are auto-pruned nightly.
                </p>
              </div>
            </div>
            {/* Iter91 — bulk actions bar. Only renders when there's at least
                one row to select. Select-all toggle mirrors gmail-style bulk
                UX; row selection persists across scroll. */}
            {[...blocked, ...warned].length > 0 && (
              <div className="flex flex-wrap items-center gap-3 mb-3 border-b border-[#262626] pb-3">
                <label className="flex items-center gap-2 text-[10px] uppercase tracking-wider text-[#A3A3A3] cursor-pointer" data-testid="signup-select-all-wrap">
                  <input
                    type="checkbox"
                    className="w-4 h-4 accent-[#CCFF00]"
                    data-testid="signup-select-all"
                    checked={selectedSignups.size > 0 && selectedSignups.size === [...blocked, ...warned].length}
                    onChange={() => toggleSelectAllSignups([...blocked, ...warned])}
                  />
                  Select all ({selectedSignups.size} / {[...blocked, ...warned].length})
                </label>
                <div className="flex-1" />
                <button
                  type="button"
                  disabled={selectedSignups.size === 0 || bulkBusy}
                  onClick={() => bulkAction("reset")}
                  className="min-h-[36px] px-3 py-1.5 text-[10px] uppercase tracking-wider border border-[#CCFF00] text-[#CCFF00] hover:bg-[#CCFF00] hover:text-black disabled:opacity-30 active:scale-95 transition-all"
                  data-testid="bulk-reset-signups"
                >
                  {bulkBusy ? "…" : `Bulk reset (${selectedSignups.size})`}
                </button>
                <button
                  type="button"
                  disabled={selectedSignups.size === 0 || bulkBusy}
                  onClick={() => bulkAction("delete")}
                  className="min-h-[36px] px-3 py-1.5 text-[10px] uppercase tracking-wider border border-[#EF4444] text-[#EF4444] hover:bg-[#EF4444] hover:text-white disabled:opacity-30 active:scale-95 transition-all"
                  data-testid="bulk-remove-signups"
                >
                  {bulkBusy ? "…" : `Bulk remove (${selectedSignups.size})`}
                </button>
              </div>
            )}
            <div className="space-y-2 max-h-[360px] overflow-y-auto pr-1">
              {[...blocked, ...warned].map((b) => {
                const count = b.abandon_count || 0;
                const isBlocked = count >= 3;
                const isSel = selectedSignups.has(b.email);
                return (
                  <div key={b.email} className="border p-3 flex flex-wrap items-center justify-between gap-3" style={{ borderColor: isBlocked ? "#EF4444" : (isSel ? "#CCFF00" : "#262626") }} data-testid={`blocked-${b.email}`}>
                    <input
                      type="checkbox"
                      className="w-4 h-4 accent-[#CCFF00] shrink-0"
                      data-testid={`signup-select-${b.email}`}
                      checked={isSel}
                      onChange={() => toggleSelectSignup(b.email)}
                    />
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <span className="text-sm text-white truncate">{b.email}</span>
                        <span
                          className="label-mono px-1.5 py-0.5"
                          style={{
                            background: isBlocked ? "#EF4444" : "#262626",
                            color: isBlocked ? "#FFFFFF" : "#A3A3A3",
                          }}
                        >
                          {count} / 3 abandoned
                        </span>
                      </div>
                      {b.last_abandoned_at && (
                        <div className="text-[10px] uppercase tracking-wider text-[#525252] mt-1">
                          last abandoned {new Date(b.last_abandoned_at).toLocaleString()}
                          {b.last_abandoned_name && <> · was {b.last_abandoned_name}</>}
                        </div>
                      )}
                    </div>
                    <button
                      type="button"
                      disabled={!!resetting[b.email]}
                      onClick={() => resetSignup(b.email)}
                      className="px-3 py-1.5 text-xs uppercase tracking-wider border border-[#CCFF00] text-[#CCFF00] hover:bg-[#CCFF00] hover:text-black inline-flex items-center gap-1 disabled:opacity-40"
                      data-testid={`reset-signup-${b.email}`}
                    >
                      {resetting[b.email] ? "…" : "Reset"}
                    </button>
                  </div>
                );
              })}
            </div>
          </section>
        );
      })()}

      {TOOLS.map((section, gi) => {
        const visible = section.items.filter((t) => canSee(t.requires));
        if (!visible.length) return null;
        return (
          <section key={section.group} className="space-y-3 fade-up" style={{ animationDelay: `${0.05 * (gi + 1)}s` }}>
            <div className="label-mono">{section.group}</div>
            <div className="grid md:grid-cols-2 gap-3">
              {visible.map((t) => (
                <Link
                  key={t.to}
                  to={t.to}
                  data-testid={`admin-tool-${t.to.replace(/[/]/g, "-").slice(1)}`}
                  className="card-surface p-5 hover:border-[#CCFF00] transition-all group block"
                >
                  <div className="flex items-start gap-4">
                    <div className="w-10 h-10 flex items-center justify-center shrink-0" style={{ background: t.accent }}>
                      <t.icon strokeWidth={2.5} className="w-5 h-5 text-black" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center justify-between gap-2 mb-1">
                        <h3 className="h-display text-xl tracking-tight">{t.title}</h3>
                        <ArrowRight className="w-4 h-4 text-[#A3A3A3] group-hover:text-[#CCFF00] group-hover:translate-x-1 transition-all shrink-0" />
                      </div>
                      <p className="text-xs text-[#A3A3A3] leading-relaxed">{t.desc}</p>
                    </div>
                  </div>
                </Link>
              ))}
            </div>
          </section>
        );
      })}

      {/* Data export — CSV of every player + their bookings + skill ratings */}
      <section className="card-surface p-6 fade-up" data-testid="data-export-panel">
        <div className="flex items-start gap-3 mb-3">
          <Download className="w-5 h-5 text-[#CCFF00] mt-0.5 shrink-0" />
          <div>
            <h3 className="h-display text-2xl mb-1">Export players CSV</h3>
            <p className="text-sm text-[#A3A3A3] leading-relaxed max-w-2xl">
              One wide CSV with every player&apos;s profile (address, DOB, guardian), every booking they&apos;ve made, their coach, payment status, plus per-skill averages and weakest skill. Drop straight into Excel for monthly reporting or accountant feeds.
            </p>
          </div>
        </div>
        <button
          type="button"
          data-testid="export-csv-btn"
          disabled={exporting}
          onClick={async () => {
            setExporting(true);
            try {
              const resp = await api.get("/admin/export/players-csv", { responseType: "blob" });
              const blob = new Blob([resp.data], { type: "text/csv" });
              const a = document.createElement("a");
              a.href = URL.createObjectURL(blob);
              const cd = resp.headers["content-disposition"] || "";
              const match = /filename="([^"]+)"/.exec(cd);
              a.download = match ? match[1] : `rallylab_players_${new Date().toISOString().slice(0, 10)}.csv`;
              document.body.appendChild(a); a.click();
              setTimeout(() => { URL.revokeObjectURL(a.href); a.remove(); }, 500);
              toast.success("Download started");
            } catch (e) {
              toast.error(formatApiError(e.response?.data?.detail) || "Export failed");
            } finally { setExporting(false); }
          }}
          className="px-5 py-3 text-xs inline-flex items-center gap-2 font-bold uppercase tracking-wider border border-[#CCFF00] text-[#CCFF00] hover:bg-[#CCFF00] hover:text-black transition-colors disabled:opacity-50"
        >
          <Download className="w-4 h-4" /> {exporting ? "Exporting…" : "Download players CSV"}
        </button>
      </section>

      {/* Automated email reminders — daily scheduler. Manual trigger for testing. */}
      <section className="card-surface p-6 fade-up" data-testid="email-jobs-panel">
        <div className="flex items-start gap-3 mb-3">
          <Mailbox className="w-5 h-5 text-[#CCFF00] mt-0.5 shrink-0" />
          <div>
            <h3 className="h-display text-2xl mb-1">Automated reminders</h3>
            <p className="text-sm text-[#A3A3A3] leading-relaxed max-w-2xl">
              The scheduler runs every day at <span className="text-white font-semibold">08:00 UTC</span> (credential expiry) and <span className="text-white font-semibold">08:30 UTC</span> (post-session follow-ups). Expiry nudges fire at 30 / 14 / 7 / 1 day before, plus daily once lapsed. Session follow-ups land 3 days after every booking and include the player&apos;s weakest skill so they have a clear next-step.
            </p>
          </div>
        </div>
        <button
          type="button"
          data-testid="run-jobs-now-btn"
          disabled={runningJobs}
          onClick={async () => {
            setRunningJobs(true);
            try {
              const { data } = await api.post("/admin/jobs/run-now");
              const ex = data.result?.credential_expiry;
              const fu = data.result?.session_followups;
              toast.success(
                `Expiry: ${ex?.total_emails || 0} email${ex?.total_emails === 1 ? "" : "s"} across ${ex?.sent_for_coaches || 0} coach${ex?.sent_for_coaches === 1 ? "" : "es"} · Follow-ups: ${fu?.sent || 0} sent`,
                { duration: 6000 }
              );
            } catch (e) {
              toast.error(formatApiError(e.response?.data?.detail) || "Could not run jobs");
            } finally { setRunningJobs(false); }
          }}
          className="px-5 py-3 text-xs inline-flex items-center gap-2 font-bold uppercase tracking-wider border border-[#CCFF00] text-[#CCFF00] hover:bg-[#CCFF00] hover:text-black transition-colors disabled:opacity-50"
        >
          <Bell className="w-4 h-4" /> {runningJobs ? "Running…" : "Run jobs now"}
        </button>
        {/* Seed the Rally Lab starter drill library. Idempotent — safe to click twice.
            Useful after a fresh deploy where /drills came up empty. Auto-run
            already happens at boot via seed.py; this is the manual re-trigger. */}
        <button
          type="button"
          data-testid="seed-drills-btn"
          disabled={seedingDrills}
          onClick={async () => {
            setSeedingDrills(true);
            try {
              const { data } = await api.post("/admin/drills/seed");
              const d = data.drills_inserted ?? 0;
              const r = data.routines_inserted ?? 0;
              if (d === 0 && r === 0) {
                toast.message("Drill library already seeded — nothing to add.");
              } else {
                toast.success(`Added ${d} drill${d === 1 ? "" : "s"} + ${r} routine${r === 1 ? "" : "s"} to the library`, { duration: 6000 });
              }
            } catch (e) {
              toast.error(formatApiError(e.response?.data?.detail) || "Could not seed drills");
            } finally { setSeedingDrills(false); }
          }}
          className="ml-2 px-5 py-3 text-xs inline-flex items-center gap-2 font-bold uppercase tracking-wider border border-[#FF4500] text-[#FF4500] hover:bg-[#FF4500] hover:text-black transition-colors disabled:opacity-50"
        >
          <Sparkles className="w-4 h-4" /> {seedingDrills ? "Seeding…" : "Seed starter drills"}
        </button>
        {/* Iter105 (Slice D) — Archive old assessment comments off the user
            docs into `assessment_history`. Idempotent; only touches entries
            older than 30 days. Keeps main player profiles clean. */}
        <button
          type="button"
          data-testid="migrate-assessment-history-btn"
          disabled={migratingHistory}
          onClick={async () => {
            if (!window.confirm("Archive assessment history entries older than 30 days into the assessment_history collection?\n\nLive entries (< 30 days) stay on the player profile. Safe to re-run.")) return;
            setMigratingHistory(true);
            try {
              const { data } = await api.post("/admin/assessment-history/migrate");
              toast.success(`Archived ${data.entries_moved || 0} entr${(data.entries_moved || 0) === 1 ? "y" : "ies"} across ${data.students_touched || 0} player${(data.students_touched || 0) === 1 ? "" : "s"}`, { duration: 6000 });
            } catch (e) {
              toast.error(formatApiError(e.response?.data?.detail) || "Migration failed");
            } finally { setMigratingHistory(false); }
          }}
          className="ml-2 px-5 py-3 text-xs inline-flex items-center gap-2 font-bold uppercase tracking-wider border border-[#A3A3A3] text-[#A3A3A3] hover:bg-[#A3A3A3] hover:text-black transition-colors disabled:opacity-50"
        >
          <FileSearch className="w-4 h-4" /> {migratingHistory ? "Archiving…" : "Archive old assessments"}
        </button>
        {/* Iter111 — One-click trigger for the /api/admin/heal-assessment-status
            healer (iter110 rewrite). Sweeps stuck assessment bookings + student
            profiles across 1:1 AND group sessions, stamps followup_sent_at so
            the dashboard reminder pills clear. Idempotent — safe to re-run. */}
        <button
          type="button"
          data-testid="heal-assessment-status-btn"
          disabled={healingAssessments}
          onClick={async () => {
            if (!window.confirm("Heal stuck assessment bookings?\n\nScans every is_assessment booking that isn't yet completed and closes the ones whose student profile already says 'completed' (or whose student is stuck in a pre-assessment state). Group sessions included. Safe to re-run.")) return;
            setHealingAssessments(true);
            try {
              const { data } = await api.post("/admin/heal-assessment-status");
              const h = data.healed || 0;
              const b = data.bookings_closed || 0;
              if (h === 0 && b === 0) {
                toast.success("Nothing to heal — everything is already in sync.");
              } else {
                toast.success(`Healed ${h} student${h === 1 ? "" : "s"}, closed ${b} booking${b === 1 ? "" : "s"}`, { duration: 6000 });
              }
            } catch (e) {
              toast.error(formatApiError(e.response?.data?.detail) || "Heal failed");
            } finally { setHealingAssessments(false); }
          }}
          className="ml-2 px-5 py-3 text-xs inline-flex items-center gap-2 font-bold uppercase tracking-wider border border-[#FF9F1C] text-[#FF9F1C] hover:bg-[#FF9F1C] hover:text-black transition-colors disabled:opacity-50"
        >
          <Wrench className="w-4 h-4" /> {healingAssessments ? "Healing…" : "Heal stuck assessments"}
        </button>
      </section>

      <section className="card-surface p-6 border-l-4 border-[#CCFF00] fade-up">
        <Sparkles className="w-5 h-5 text-[#CCFF00] mb-2" />
        <h3 className="h-display text-2xl mb-2">Coach access policy</h3>
        <p className="text-sm text-[#A3A3A3] leading-relaxed max-w-2xl">
          Site owners and admins (you) can view every player&apos;s progress. Regular coaches only see players
          who have booked a session with them. For 1:1 and Performance sessions, coaches have a {" "}
          <span className="text-[#CCFF00] font-bold">48-hour window</span> after the session ends to submit
          ratings, focus notes, and AI reports — group and assessment sessions don&apos;t need feedback so
          there&apos;s no window.
        </p>
      </section>

      <section className="card-surface p-6 fade-up">
        <Settings className="w-5 h-5 text-[#A3A3A3] mb-2" />
        <h3 className="h-display text-2xl mb-2">Your account</h3>
        <div className="grid md:grid-cols-2 gap-4 text-sm">
          <div>
            <div className="label-mono mb-1">EMAIL</div>
            <div className="text-white">{user?.email}</div>
          </div>
          <div>
            <div className="label-mono mb-1">SECURITY</div>
            <Link to="/forgot-password" className="text-[#CCFF00] hover:underline" data-testid="admin-change-password">
              Reset my password →
            </Link>
          </div>
        </div>
      </section>

      {/* Danger zone — only super_admin can wipe sample data */}
      {isSuperAdmin && (
        <section className="card-surface p-6 border-l-4 border-[#FF4500] fade-up" data-testid="danger-zone">
          <div className="flex items-start gap-3 mb-4">
            <Trash2 className="w-5 h-5 text-[#FF4500] mt-0.5 shrink-0" />
            <div>
              <h3 className="h-display text-2xl mb-1">Danger zone</h3>
              <p className="text-sm text-[#A3A3A3] leading-relaxed max-w-2xl">
                Wipe every offering, template, booking, skill rating, request, availability block and audit log. <span className="text-white font-semibold">User accounts are preserved</span>. Use this when you&apos;re ready to launch and want a 100% coach-owned dataset.
              </p>
            </div>
          </div>
          <button
            type="button"
            data-testid="wipe-sample-data-btn"
            disabled={wiping}
            onClick={async () => {
              const a = window.prompt("This will permanently delete ALL operational data (sessions, bookings, ratings, audit logs).\n\nUser accounts will be kept.\n\nType DELETE to confirm:");
              if (a !== "DELETE") { toast.message("Cancelled"); return; }
              setWiping(true);
              try {
                const { data } = await api.post("/admin/wipe-sample-data");
                const total = Object.values(data.wiped || {}).reduce((a, b) => a + (typeof b === "number" ? b : 0), 0);
                toast.success(`Wiped ${total} records across ${Object.keys(data.wiped || {}).length} collections`);
              } catch (e) {
                toast.error(formatApiError(e.response?.data?.detail) || "Wipe failed");
              } finally { setWiping(false); }
            }}
            className="px-5 py-3 text-xs inline-flex items-center gap-2 font-bold uppercase tracking-wider border border-[#FF4500] text-[#FF4500] hover:bg-[#FF4500] hover:text-black transition-colors disabled:opacity-50"
          >
            <Trash2 className="w-4 h-4" /> {wiping ? "Wiping…" : "Wipe sample data"}
          </button>
        </section>
      )}
    </div>
  );
}
